import { Children, cloneElement, FC, isValidElement, ReactNode, useEffect, useRef, useState } from "react";

import { Stack, styled, Typography } from "@mui/material";
import { NewSearchTextBox } from "../../../features/data-under-management/components/NewSearchTextBox";
import { useDebounce } from "../../../lib/Hooks";
import { useBackfillStore } from "../../../store/backfillsStore";
import { ProgressBarTitle } from "../ProgressBarTitle";

const FullContainerStack = styled(Stack)({
  width: "100%",
});

const DynamicDiagramSmallContainer = styled(Stack)({
  overflowY: "auto",
  "&:hover .hide": {
    visibility: "visible",
  },

  ".hide": {
    visibility: "hidden",
  },
});

interface IAditionalSelections {
  title: string;
  children: ReactNode;
  completedPercentage: number;
}

const AditionalSelections: FC<IAditionalSelections> = ({ title, children, completedPercentage }) => {
  const subgraph = useBackfillStore((state) => state.subgraph);
  const updateGraph = useBackfillStore((state) => state.updateGraphFilters);

  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const checkOverflow = () => {
    const container = containerRef.current;
    if (container) {
      setHasOverflow(container.scrollHeight > container.clientHeight);
    }
  };

  useEffect(() => {
    updateGraph(debouncedSearchValue);
  }, [debouncedSearchValue, updateGraph]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const resizeObserver = new ResizeObserver(() => checkOverflow());
    resizeObserver.observe(container);
    checkOverflow();
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <FullContainerStack spacing={2}>
      <ProgressBarTitle title={title} completedPercentage={completedPercentage} />
      <Stack spacing={1} sx={{ width: "100%" }}>
        <Typography variant="body.medium.regular">
          The selected upload is connected to these downloads and uploads.
        </Typography>
        <Typography variant="body.small.regular" sx={{ mt: 2 }}>
          <b>Hover</b> over an upload to see its connected downloads.
        </Typography>
        <Typography variant="body.small.regular" sx={{ mt: 2 }}>
          <b>Click</b> on one or more uploads to add them to this backfill run.
        </Typography>
      </Stack>
      <Stack sx={{ width: "340px" }}>
        <NewSearchTextBox
          value={searchValue}
          onChange={setSearchValue}
          placeholder="Filter by upload name"
          width="340px"
        />
      </Stack>
      <Stack sx={{ maxHeight: "40vh" }}>
        <DynamicDiagramSmallContainer ref={containerRef}>
          {subgraph !== undefined &&
            Children.map(children, (child) =>
              isValidElement<{ showIcons: boolean }>(child)
                ? cloneElement(child, { showIcons: Boolean(!hasOverflow) })
                : child
            )}
        </DynamicDiagramSmallContainer>
      </Stack>
    </FullContainerStack>
  );
};

export { AditionalSelections };
